import { Link } from "react-router-dom";
import { 
  Paintbrush, Home, Palette, Layers, Hammer, Eye,
  ArrowRight, CheckCircle, Phone, Star, Shield, Clock, Award
} from "lucide-react";
import { Button } from "@/components/ui/button";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";

const services = [
  { icon: Home, title: "Interior Painting", slug: "interior-painting", desc: "Transform your living spaces with flawless interior painting — walls, ceilings, and trim.", image: "https://images.unsplash.com/photo-1562259949-e8e7689d7828?w=600&q=80" },
  { icon: Paintbrush, title: "Exterior Painting", slug: "exterior-painting", desc: "Boost curb appeal with weather-resistant exterior painting built for the Pacific Northwest.", image: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=600&q=80" },
  { icon: Layers, title: "Cabinet Painting", slug: "cabinet-painting", desc: "Refresh your kitchen for a fraction of the cost of replacement with professional cabinet painting.", image: "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=600&q=80" },
  { icon: Hammer, title: "Deck & Fence Staining", slug: "deck-fence-staining", desc: "Restore and protect outdoor wood surfaces with professional staining and sealing.", image: "https://images.unsplash.com/photo-1591825729269-caeb344f6df2?w=600&q=80" },
  { icon: Eye, title: "Drywall Repair & Prep", slug: "drywall-repair", desc: "Perfect walls start with perfect prep — patching, sanding, and priming for a flawless finish.", image: "https://images.unsplash.com/photo-1504148455328-c376907d081c?w=600&q=80" },
  { icon: Palette, title: "Color Consultation", slug: "color-consultation", desc: "Not sure which colors? Our experts help you find the perfect palette — free with any project.", image: "https://images.unsplash.com/photo-1615529328331-f8917597711f?w=600&q=80" },
];

const processSteps = [
  { num: "01", title: "Describe Your Project", desc: "Tell us what needs painting — send photos if you can" },
  { num: "02", title: "Get a Free Quote", desc: "We provide a clear, honest estimate within 24 hours" },
  { num: "03", title: "We Paint It Right", desc: "Our expert painters complete the work on schedule" },
  { num: "04", title: "Love Your Space Again", desc: "Enjoy stunning results with our satisfaction guarantee" },
];

const DECK_BEFORE = "/assets/images/beforeafter/deck-before.webp";
const DECK_AFTER = "/assets/images/beforeafter/deck-after.webp";
const WOODROT_BEFORE = "/assets/images/beforeafter/woodrot-before.webp";
const WOODROT_AFTER = "/assets/images/beforeafter/woodrot-after.webp";
const EXTERIOR_BEFORE = "/assets/images/beforeafter/exterior-before.jpeg";
const EXTERIOR_AFTER = "/assets/images/beforeafter/exterior-after.png";
const INTERIORDOOR_BEFORE = "/assets/images/beforeafter/interior-before.webp";
const INTERIORDOOR_AFTER = "/assets/images/beforeafter/interior-after.webp";

const stats = [
  { value: "30+", label: "Years Experience" },
  { value: "500+", label: "Projects Completed" },
  { value: "100%", label: "Satisfaction Rate" },
  { value: "Free", label: "Estimates" },
];

export const HomePage = () => {
  return (
    <div className="bg-white" data-testid="home-page">

      {/* ─── HERO ─── */}
      <section className="relative min-h-screen overflow-hidden" data-testid="hero-section">
        {/* Background image */}
        <div className="absolute inset-0">
          <img 
            src="/assets/images/hero-painter.png" 
            alt="Professional Painter Pouring Premium Paint"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0c2340]/95 via-[#0c2340]/85 to-[#0c2340]/50"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-40 pb-32 min-h-screen flex items-center">
          <div className="max-w-2xl">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 mb-8 animate-fadeInUp">
              <span className="w-2 h-2 bg-[#f97316] rounded-full animate-pulse"></span>
              <span className="text-white/90 text-sm font-medium tracking-wide">Professional Painting Services</span>
            </div>
            
            {/* Main Heading */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl text-white leading-[1.08] mb-8 font-extrabold animate-fadeInUp delay-100" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Your Home,
              <br />
              <span className="text-[#f97316]">Our Canvas.</span>
            </h1>
            
            {/* Description */}
            <p className="text-gray-300 text-lg lg:text-xl leading-relaxed mb-10 max-w-xl animate-fadeInUp delay-200">
              From interior walls to exterior siding, Vanguard Painting Masters delivers 
              premium painting services with flawless, lasting results.
            </p>
            
            {/* CTA Buttons */}
            <div className="flex flex-wrap items-center gap-4 mb-12 animate-fadeInUp delay-300">
              <Link to="/contact">
                <Button 
                  className="bg-[#f97316] hover:bg-[#ea580c] text-white font-bold px-8 py-6 rounded-lg text-base shadow-lg shadow-orange-500/25 hover:shadow-orange-500/40 transition-all"
                  data-testid="hero-get-quote-btn"
                >
                  Get Free Quote <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <a href="tel:2368681195">
                <Button variant="outline" className="border-2 border-white/30 text-white hover:bg-white/10 font-bold px-8 py-6 rounded-lg text-base backdrop-blur-sm">
                  <Phone className="w-5 h-5 mr-2" /> (236)868-1195
                </Button>
              </a>
            </div>

            {/* Trust Indicators */}
            <div className="flex flex-wrap items-center gap-6 animate-fadeInUp delay-400">
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-[#f97316]" />
                <span className="text-gray-300 text-sm">Licensed & Insured</span>
              </div>
              <div className="flex items-center gap-2">
                <Star className="w-5 h-5 text-[#f97316]" />
                <span className="text-gray-300 text-sm">500+ Happy Customers</span>
              </div>
              <div className="flex items-center gap-2">
                <Award className="w-5 h-5 text-[#f97316]" />
                <span className="text-gray-300 text-sm">30+ Years Experience</span>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 animate-bounce-soft">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center pt-2">
            <div className="w-1.5 h-3 bg-[#f97316] rounded-full"></div>
          </div>
        </div>
      </section>

      {/* ─── STATS STRIP ─── */}
      <section className="bg-[#0c2340] py-10" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4">
            {stats.map((stat, i) => (
              <div key={stat.label} className={`text-center py-4 px-6 ${i > 0 ? 'border-l border-white/10' : ''}`}>
                <div className="text-3xl lg:text-4xl font-extrabold text-[#f97316] mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{stat.value}</div>
                <p className="text-gray-400 text-sm">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── ABOUT PREVIEW ─── */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
            {/* Zig-zag Images */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <img 
                  src="https://images.unsplash.com/photo-1768527341242-62055b63260b?w=400&q=80" 
                  alt="Deck renovation" 
                  className="w-full h-40 object-cover rounded-xl shadow-lg" 
                />
                <img 
                  src="https://images.unsplash.com/photo-1562259949-e8e7689d7828?w=400&q=80" 
                  alt="Interior painting" 
                  className="w-full h-52 object-cover rounded-xl shadow-lg" 
                />
              </div>
              <div className="space-y-4 pt-8">
                <img 
                  src="https://images.unsplash.com/photo-1581858726788-75bc0f6a952d?w=400&q=80" 
                  alt="Cabinet painting" 
                  className="w-full h-52 object-cover rounded-xl shadow-lg" 
                />
                <img 
                  src="https://images.unsplash.com/photo-1591825729269-caeb344f6df2?w=400&q=80" 
                  alt="Deck staining" 
                  className="w-full h-40 object-cover rounded-xl shadow-lg" 
                />
              </div>
              {/* Experience badge */}
              <div className="absolute-hidden lg:block col-span-2 flex justify-center -mt-8">
                <div className="bg-[#f97316] rounded-xl px-6 py-4 text-white shadow-xl inline-flex items-center gap-3">
                  <div className="text-3xl font-extrabold leading-none" style={{ fontFamily: 'Outfit, sans-serif' }}>30+</div>
                  <div className="text-white/90 text-sm font-medium">Years of<br/>Experience</div>
                </div>
              </div>
            </div>

            {/* Content */}
            <div>
              <div className="inline-flex items-center gap-2 bg-orange-50 border border-orange-200 rounded-full px-4 py-2 mb-6">
                <span className="w-2 h-2 bg-[#f97316] rounded-full"></span>
                <span className="text-[#f97316] font-semibold text-sm">About Vanguard</span>
              </div>
              <h2 className="text-3xl lg:text-4xl xl:text-5xl font-extrabold text-[#1a1a2e] mb-6 leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Painting Experts Who
                <span className="text-[#f97316]"> Care About </span>
                Your Home
              </h2>
              <p className="text-gray-500 text-lg leading-relaxed mb-8">
                With over 30 years of combined experience, our team specializes in interior and exterior 
                painting, cabinet refinishing, deck staining, and color consultation. We pride ourselves 
                on clean work, honest pricing, and beautiful results.
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
                {[
                  { icon: Shield, text: "Licensed & Fully Insured" },
                  { icon: Clock, text: "On-Time, Every Time" },
                  { icon: Award, text: "Quality Guaranteed" },
                  { icon: Star, text: "5-Star Customer Service" },
                ].map((item) => (
                  <div key={item.text} className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-3">
                    <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-[#f97316]" />
                    </div>
                    <span className="text-[#1a1a2e] font-semibold text-sm">{item.text}</span>
                  </div>
                ))}
              </div>

              <Link to="/about">
                <Button className="bg-[#f97316] hover:bg-[#ea580c] text-white font-bold px-8 py-6 rounded-lg shadow-lg shadow-orange-500/20">
                  Learn More About Us <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ─── SERVICES ─── */}
      <section className="py-24 lg:py-32 bg-[#0c2340]" data-testid="services-preview-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/10 rounded-full px-4 py-2 mb-6">
              <span className="w-2 h-2 bg-[#f97316] rounded-full"></span>
              <span className="text-white/80 font-semibold text-sm">What We Offer</span>
            </div>
            <h2 className="text-3xl lg:text-4xl xl:text-5xl text-white leading-tight font-extrabold" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Professional Services for <span className="text-[#f97316]">Every Painting Project</span>
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-12">
            {services.map((s, i) => (
              <Link 
                key={s.title} 
                to={`/services/${s.slug}`}
                className="group text-center"
                data-testid={`service-card-${i}`}
              >
                <div className="relative rounded-xl overflow-hidden mb-3 aspect-square">
                  <img 
                    src={s.image} 
                    alt={s.title} 
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" 
                  />
                  <div className="absolute inset-0 bg-black/30 group-hover:bg-[#f97316]/60 transition-colors duration-300 flex items-center justify-center">
                    <s.icon className="w-8 h-8 text-white opacity-80 group-hover:opacity-100 group-hover:scale-110 transition-all" />
                  </div>
                </div>
                <h3 className="text-white font-bold text-sm group-hover:text-[#f97316] transition-colors" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {s.title}
                </h3>
              </Link>
            ))}
          </div>

          <div className="text-center">
            <Link to="/services/interior-painting">
              <Button className="bg-[#f97316] hover:bg-[#ea580c] text-white font-bold px-8 py-6 rounded-lg shadow-lg shadow-orange-500/25">
                View All Services <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ─── BEFORE / AFTER ─── */}
      <section className="py-24 lg:py-32 bg-[#0c2340] relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#f97316]/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-72 h-72 bg-[#f97316]/5 rounded-full translate-y-1/2 -translate-x-1/2"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left - Text Content */}
            <div>
              <div className="inline-flex items-center gap-2 bg-white/10 border border-white/10 rounded-full px-4 py-2 mb-6">
                <span className="w-2 h-2 bg-[#f97316] rounded-full"></span>
                <span className="text-white/80 font-semibold text-sm">Our Work</span>
              </div>
              <h2 className="text-3xl lg:text-4xl xl:text-5xl font-extrabold text-white mb-6 leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                See The <span className="text-[#f97316]">Vanguard</span>
                <br />Difference
              </h2>
              <p className="text-gray-400 text-lg leading-relaxed mb-8 max-w-md">
                Real projects, real results. Drag the slider to see the transformation we bring to every home.
              </p>
              <div className="flex items-center gap-6 mb-10">
                <div className="text-center">
                  <div className="text-3xl font-extrabold text-[#f97316]" style={{ fontFamily: 'Outfit, sans-serif' }}>500+</div>
                  <p className="text-gray-400 text-xs mt-1">Projects Done</p>
                </div>
                <div className="w-px h-12 bg-white/10"></div>
                <div className="text-center">
                  <div className="text-3xl font-extrabold text-[#f97316]" style={{ fontFamily: 'Outfit, sans-serif' }}>100%</div>
                  <p className="text-gray-400 text-xs mt-1">Satisfaction</p>
                </div>
                <div className="w-px h-12 bg-white/10"></div>
                <div className="text-center">
                  <div className="text-3xl font-extrabold text-[#f97316]" style={{ fontFamily: 'Outfit, sans-serif' }}>30+</div>
                  <p className="text-gray-400 text-xs mt-1">Years Exp.</p>
                </div>
              </div>
              <Link to="/portfolio">
                <Button className="bg-[#f97316] hover:bg-[#ea580c] text-white font-bold px-8 py-6 rounded-lg shadow-lg shadow-orange-500/25">
                  View Full Portfolio <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>

            {/* Right - 2x2 Slider Grid (smaller, zig-zag) */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <div className="bg-[#15365a] rounded-xl p-2 shadow-lg">
                  <BeforeAfterSlider
                    beforeImage={DECK_BEFORE}
                    afterImage={DECK_AFTER}
                    className="aspect-square rounded-lg overflow-hidden"
                  />
                  <p className="text-white font-bold text-xs text-center py-2">Deck Staining</p>
                </div>
                <div className="bg-[#15365a] rounded-xl p-2 shadow-lg">
                  <BeforeAfterSlider
                    beforeImage={EXTERIOR_BEFORE}
                    afterImage={EXTERIOR_AFTER}
                    className="aspect-square rounded-lg overflow-hidden"
                  />
                  <p className="text-white font-bold text-xs text-center py-2">Exterior Painting</p>
                </div>
              </div>
              <div className="space-y-4">
                <div className="bg-[#15365a] rounded-xl p-2 shadow-lg">
                  <BeforeAfterSlider
                    beforeImage={WOODROT_BEFORE}
                    afterImage={WOODROT_AFTER}
                    className="aspect-square rounded-lg overflow-hidden"
                  />
                  <p className="text-white font-bold text-xs text-center py-2">Wood Staining</p>
                </div>
                <div className="bg-[#15365a] rounded-xl p-2 shadow-lg">
                  <BeforeAfterSlider
                    beforeImage={INTERIORDOOR_BEFORE}
                    afterImage={INTERIORDOOR_AFTER}
                    className="aspect-square rounded-lg overflow-hidden"
                  />
                  <p className="text-white font-bold text-xs text-center py-2">Door Refinishing</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── HOW IT WORKS ─── */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-orange-50 border border-orange-200 rounded-full px-4 py-2 mb-6 mx-auto">
              <span className="w-2 h-2 bg-[#f97316] rounded-full"></span>
              <span className="text-[#f97316] font-semibold text-sm">How It Works</span>
            </div>
            <h2 className="text-3xl lg:text-4xl xl:text-5xl font-extrabold text-[#1a1a2e]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Simple Process, <span className="text-[#f97316]">Great Results</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {processSteps.map((step, i) => (
              <div key={step.num} className="relative text-center group" data-testid={`process-${i}`}>
                {/* Connector line */}
                {i < processSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-10 left-[60%] w-[80%] h-[2px] bg-gradient-to-r from-[#f97316] to-orange-200 z-0"></div>
                )}
                {/* Number circle */}
                <div className="relative z-10 w-20 h-20 bg-[#f97316] rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-orange-500/20 group-hover:scale-110 transition-transform">
                  <span className="text-2xl font-extrabold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>{step.num}</span>
                </div>
                <h4 className="font-bold text-[#1a1a2e] text-xl mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>{step.title}</h4>
                <p className="text-gray-500 text-sm leading-relaxed max-w-[250px] mx-auto">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── CTA ─── */}
      <section className="relative py-24 lg:py-32 overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 bg-[#f97316]">
          <div className="absolute -top-32 -right-32 w-96 h-96 bg-white/10 rounded-full"></div>
          <div className="absolute -bottom-24 -left-24 w-72 h-72 bg-white/5 rounded-full"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-white/5 rounded-full"></div>
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="flex justify-center gap-1 mb-6">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="w-6 h-6 text-white fill-white" />
            ))}
          </div>
          <h2 className="text-3xl lg:text-5xl font-extrabold text-white mb-6 leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Ready to Transform
            <br />Your Home?
          </h2>
          <p className="text-white/90 text-lg mb-10 max-w-xl mx-auto">
            No project is too big or small. Get a free, no-obligation estimate 
            on your next painting project.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact">
              <Button className="bg-white text-[#f97316] hover:bg-gray-100 font-bold rounded-lg px-8 py-6 text-base shadow-xl" data-testid="cta-get-quote-btn">
                Get a Free Quote <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <a href="tel:2368681195">
              <Button variant="outline" className="border-2 border-white text-white hover:bg-white/10 font-bold rounded-lg px-8 py-6 text-base">
                <Phone className="w-5 h-5 mr-2" /> (236)868-1195
              </Button>
            </a>
          </div>
        </div>
      </section>

    </div>
  );
};

export default HomePage;
